import type { MockUser } from '@/types';

type AgeAwareUser = Pick<MockUser, 'ageGroup' | 'birthdate'> | null | undefined;

export function getAgeFromBirthdate(birthdate?: string | null): number | null {
  if (!birthdate) return null;

  const parsed = new Date(birthdate);
  if (Number.isNaN(parsed.getTime())) return null;

  const today = new Date();
  if (parsed > today) return null;

  let age = today.getFullYear() - parsed.getFullYear();
  const monthDiff = today.getMonth() - parsed.getMonth();
  const dayDiff = today.getDate() - parsed.getDate();

  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) age--;

  return age;
}

/** Inclusive age range a member must fall in when they answer "not 18 or older". */
export const MINOR_MIN_AGE = 13;
export const MINOR_MAX_AGE = 17;

/**
 * Why a birthdate answer was refused (MWBI-899).
 *
 * These codes mirror the server's AgeAnswerRejection in
 * cms/server/src/utils/age-verification.ts one-for-one, so the same rejection
 * renders the same localized sentence whether it was caught here or returned by
 * /users/me/verify-age. The server is authoritative — this check exists so the
 * member gets an answer without a round trip, not to decide anything.
 */
export type BirthdateRejection =
  | 'BIRTHDATE_REQUIRED'
  | 'BIRTHDATE_INVALID'
  | 'BIRTHDATE_IN_FUTURE'
  | 'AGE_OUT_OF_RANGE';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parse a strict YYYY-MM-DD string into a local-midnight Date, or null.
 *
 * Mirrors the authoritative server parser (parseIsoDate in
 * cms/server/src/utils/age-verification.ts) one-for-one. `new Date(birthdate)`
 * cannot be trusted for validation: it reads a date-only ISO string as UTC while
 * getFullYear()/getDate() report local time (an off-by-one on the birthday), and
 * it accepts locale formats the server rejects. Parsing the parts and
 * round-tripping keeps this client check in lockstep with the server, so a date
 * never passes the modal only to be rejected by /users/me/verify-age (MWBI-899).
 */
function parseIsoDate(value: string): Date | null {
  const match = ISO_DATE.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  const parsed = new Date(year, month - 1, day);
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null;
  }

  return parsed;
}

/**
 * Validate the birthdate given by a member who said they are not 18 or older.
 *
 * Returns null when the date is acceptable. A future date is reported as its own
 * reason rather than as an out-of-range age, so the member is told to check the
 * date instead of being told they are too young.
 */
export function validateMinorBirthdate(birthdate?: string | null): BirthdateRejection | null {
  if (!birthdate || birthdate.trim() === '') return 'BIRTHDATE_REQUIRED';

  // Strict YYYY-MM-DD parse, matching the authoritative server validator. A bare
  // new Date(birthdate) accepted locale formats and UTC-shifted dates the server
  // rejects, producing a "valid locally, rejected on submit" round trip (MWBI-899).
  const parsed = parseIsoDate(birthdate.trim());
  if (!parsed) return 'BIRTHDATE_INVALID';

  const now = new Date();
  // A future date gets its own reason so the member is told to check the date
  // rather than being told they are too young.
  if (parsed.getTime() > now.getTime()) return 'BIRTHDATE_IN_FUTURE';

  // Age from the strictly-parsed local date -- not getAgeFromBirthdate, which
  // re-parses the string with new Date() and would reintroduce the UTC drift.
  let age = now.getFullYear() - parsed.getFullYear();
  const monthDiff = now.getMonth() - parsed.getMonth();
  const dayDiff = now.getDate() - parsed.getDate();
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) age--;

  if (age < MINOR_MIN_AGE || age > MINOR_MAX_AGE) return 'AGE_OUT_OF_RANGE';

  return null;
}

export function canCreateGroupsForUser(user?: AgeAwareUser): boolean {
  if (!user) return false;

  // age_13_to_15 is a hard-deny on the backend (is-16-plus policy) regardless of birthdate.
  // Mirror it here so the UI doesn't allow a submission that the server will reject with a 403.
  if (user.ageGroup === 'age_13_to_15') return false;

  const age = getAgeFromBirthdate(user.birthdate);
  if (age !== null) return age >= 16;

  if (user.ageGroup === 'under_18') return false;
  if (user.ageGroup === 'age_18_plus' || user.ageGroup === 'age_16_plus') return true;

  return true;
}

export function canUseMessaging(user?: AgeAwareUser): boolean {
  if (!user) return false;

  return user.ageGroup === 'age_18_plus';
}